<template>
  <div class="pa-4">
    <SnackbarComponent
      v-model="snackbar.show"
      :message="snackbar.message"
      :color="snackbar.color"
    />
    <v-card
      flat
      rounded="lg"
      class="pa-4"
      style="border: 1px solid rgba(0, 0, 0, 0.08)"
    >
      <div class="text-h6 mb-4">License Settings</div>
      <!-- <div class="mb-4">
        <v-text-field
          v-model="grace_period"
          label="Default Grace Period (Days)"
          type="number"
          variant="outlined"
          density="comfortable"
          hide-details
          @keypress="allowNumbersOnly"
          @update:model-value="
            (val) => sanitizeNumberInput('grace_period', val)
          "
        />
      </div> -->
      <v-radio-group v-model="selectedType" inline>
        <v-radio label="Annual Licensing" value="ANNUAL" />
        <v-radio label="Trial Period" value="TRIAL" />
      </v-radio-group>

      <!-- Annual Licensing Settings -->
      <v-expand-transition>
        <div v-if="selectedType === 'ANNUAL'">
          <v-divider class="my-4" />
          <div class="text-subtitle-1 mb-3">Annual Licensing</div>

          <v-text-field
            v-model="annual.amount"
            label="Per User Annual Plan"
            type="number"
            density="comfortable"
            variant="outlined"
            hide-details
            class="mb-3"
            @keypress="allowNumbersOnly"
            @update:model-value="
              (val) => sanitizeNumberInput('annual.amount', val)
            "
          />

          <v-text-field
            v-model="annual.users"
            label="Number of Users"
            type="number"
            variant="outlined"
            density="comfortable"
            hide-details
            @keypress="allowNumbersOnly"
            @update:model-value="
              (val) => sanitizeNumberInput('annual.users', val)
            "
          />
        </div>
      </v-expand-transition>

      <!-- Trial Period Settings -->
      <v-expand-transition>
        <div v-if="selectedType === 'TRIAL'">
          <v-divider class="my-4" />
          <div class="text-subtitle-1 mb-3">Trial Period</div>

          <v-text-field
            v-model="trial.days"
            label="Number of Days"
            type="number"
            density="comfortable"
            variant="outlined"
            hide-details
            class="mb-3"
            @keypress="allowNumbersOnly"
            @update:model-value="
              (val) => sanitizeNumberInput('annual.users', val)
            "
          />

          <v-text-field
            v-model="trial.users"
            label="Number of Users"
            type="number"
            density="comfortable"
            variant="outlined"
            hide-details
          />
        </div>
      </v-expand-transition>

      <v-divider class="my-6" />

      <div class="d-flex justify-end" @click="saveSettings">
        <v-btn color="primary" variant="flat" :loading="loading">
          Save Settings
        </v-btn>
      </div>
    </v-card>

    <v-card
      flat
      rounded="lg"
      class="pa-4 mt-4"
      style="border: 1px solid rgba(0, 0, 0, 0.08)"
    >
      <div class="text-h6 mb-2">Exposed API Access</div>

      <div class="text-body-2 text-medium-emphasis mb-4">
        Default limits{{ defaultShowApiCallCost ? " and costs" : "" }} applied
        when Exposed API Access is enabled for an organization.
      </div>

      <v-checkbox
        v-model="defaultShowApiCallCost"
        label="Show API call cost"
        color="primary"
        hide-details
        class="mb-4"
        :disabled="exposedApiLoading"
      />

      <v-row
        dense
        v-for="(api, index) in exposedApiConfig"
        :key="api.api_name"
        class="mb-2"
      >
        <v-col cols="12" :md="defaultShowApiCallCost ? 4 : 6">
          <v-text-field
            :model-value="getExposedApiLabel(api.api_name)"
            label="API Name"
            variant="outlined"
            density="compact"
            readonly
            hide-details
          />
        </v-col>
        <v-col
          :cols="defaultShowApiCallCost ? 6 : 12"
          :md="defaultShowApiCallCost ? 4 : 6"
        >
          <v-text-field
            v-model.number="exposedApiConfig[index].limit"
            label="Limit*"
            type="number"
            min="0"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :disabled="exposedApiLoading"
          />
        </v-col>
        <v-col v-if="defaultShowApiCallCost" cols="6" md="4">
          <v-text-field
            v-model.number="exposedApiConfig[index].cost_per_call"
            label="Cost Per Call*"
            type="number"
            min="0"
            step="0.01"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :disabled="exposedApiLoading"
          />
        </v-col>
      </v-row>

      <v-divider class="my-4" />

      <div class="d-flex justify-end">
        <v-btn
          color="primary"
          variant="flat"
          :loading="exposedApiLoading"
          @click="saveExposedApiSettings"
        >
          Save Exposed API Settings
        </v-btn>
      </div>
    </v-card>

    <v-card
      flat
      rounded="lg"
      class="pa-4 mt-4"
      style="border: 1px solid rgba(0, 0, 0, 0.08)"
    >
      <div class="text-h6 mb-4">Portal Settings</div>

      <v-checkbox
        v-model="portalMaintenanceEnabled"
        label="Enable portal maintenance"
        color="primary"
        hide-details
        density="comfortable"
        :disabled="portalLoading"
      />

      <div class="text-body-2 text-medium-emphasis mt-1 mb-4">
        When enabled, the portal shows a maintenance state for users.
      </div>

      <v-checkbox
        v-model="portalReleaseNotesEnabled"
        label="Enable portal release notes"
        color="primary"
        hide-details
        density="comfortable"
        :disabled="portalLoading"
      />

      <div class="text-body-2 text-medium-emphasis mt-1 mb-4">
        When enabled, the portal shows release notes to users.
      </div>

      <v-divider class="my-4" />

      <div class="d-flex justify-end">
        <v-btn
          color="primary"
          variant="flat"
          :loading="portalLoading"
          @click="savePortalSettings()"
        >
          Save Portal Settings
        </v-btn>
      </div>
    </v-card>

    <v-card
      flat
      rounded="lg"
      class="pa-4 mt-4"
      style="border: 1px solid rgba(0, 0, 0, 0.08)"
    >
      <div class="text-h6 mb-4">RCS Support Settings</div>

      <div class="setting-card">
        <div class="setting-info">
          <div class="setting-title">Auto-close Inactive cases</div>
          <div class="setting-desc">
            Cases with no activity for this many days are marked Auto-closed
          
          </div>
        </div>  

        <div class="setting-controls">
          <v-text-field
            v-model="autoCloseDays"
            class="days-input"
            type="number"
            min="1"
            variant="outlined"
            density="compact"
            hide-details
            :disabled="autoCloseLoading"
            @keypress="allowNumbersOnly"
          />
          <span class="days-label">days</span>
        </div>
      </div>

      <div class="setting-card mt-4">
        <div class="setting-info">
          <div class="setting-title">Ticket CC Emails</div>
          <div class="setting-desc">
            These email addresses are copied on support ticket notifications.
            Type an email and press Enter to add it.
          </div>
        </div>

        <div class="setting-controls cc-email-controls">
          <v-combobox
            v-model="ticketCcEmailIds"
            class="cc-email-input"
            label="CC Email IDs"
            placeholder="Type an email and press Enter"
            multiple
            chips
            closable-chips
            variant="outlined"
            density="compact"
            hide-details="auto"
            :disabled="autoCloseLoading"
          />
        </div>
      </div>

      <v-divider class="my-4" />

      <div class="d-flex justify-end">
        <v-btn
          color="primary"
          variant="flat"
          :loading="autoCloseSaving"
          :disabled="autoCloseLoading"
          @click="saveAutoCloseDays"
        >
          Save RCS Support Settings
        </v-btn>
      </div>
    </v-card>
  </div>
</template>

<script>
import { generateClient } from "aws-amplify/api";
import {
  set_bms_global_settings,
  set_auto_close_days,
  set_portal_maintenance,
  set_portal_release_notes,
} from "@/graphql/mutations";
import { get_auto_close_days } from "@/graphql/queries";
import GetLicense from "@/mixins/Licensesetting/GetLicense";
const client = generateClient();
import SnackbarComponent from "@/components/Extras/Snackbar.vue";

const EXPOSED_API_LABELS = {
  list_facilities: "List Facilities",
  list_facility_residents: "List Facility Residents",
  list_resident_recommendations: "List Resident Recommendations",
  list_resident_medications: "List Resident Medications",
  list_reports: "List Reports",
  list_all_recommendations: "List All Recommendations",
  list_facility_medications: "List Facility Medications",
};

const DEFAULT_EXPOSED_API_CONFIG = [
  { api_name: "list_facilities", limit: 5, cost_per_call: 0.02 },
  { api_name: "list_facility_residents", limit: 10, cost_per_call: 0.02 },
  {
    api_name: "list_resident_recommendations",
    limit: 13,
    cost_per_call: 0.02,
  },
  { api_name: "list_resident_medications", limit: 7, cost_per_call: 0.02 },
  { api_name: "list_reports", limit: 9, cost_per_call: 0.02 },
  { api_name: "list_all_recommendations", limit: 10, cost_per_call: 0.02 },
  { api_name: "list_facility_medications", limit: 10, cost_per_call: 0.02 },
];

function parseDefaultApiEndpointSettings(raw) {
  if (!raw) return {};
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (Array.isArray(parsed)) {
      return parsed.reduce((acc, item) => {
        if (item?.api_name) {
          acc[item.api_name] = {
            limit: Number(item.limit) || 0,
            cost_per_call: Number(item.cost_per_call) || 0,
          };
        }
        return acc;
      }, {});
    }
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (error) {
    console.error("Error parsing default_api_endpoint_settings:", error);
    return {};
  }
}

function buildExposedApiConfig(existing = {}) {
  const existingMap = Array.isArray(existing)
    ? parseDefaultApiEndpointSettings(existing)
    : existing && typeof existing === "object"
      ? existing
      : {};

  return DEFAULT_EXPOSED_API_CONFIG.map((defaults) => {
    const match = existingMap[defaults.api_name] || {};
    return {
      api_name: defaults.api_name,
      limit:
        match?.limit !== undefined && match?.limit !== null
          ? Number(match.limit)
          : defaults.limit,
      cost_per_call:
        match?.cost_per_call !== undefined && match?.cost_per_call !== null
          ? Number(match.cost_per_call)
          : defaults.cost_per_call,
    };
  });
}

function rowsToDefaultApiEndpointSettings(rows = []) {
  return rows.reduce((acc, api) => {
    acc[api.api_name] = {
      limit: Number(api.limit) || 0,
      cost_per_call: Number(api.cost_per_call) || 0,
    };
    return acc;
  }, {});
}

export default {
  name: "LicenseSettings",
  mixins: [GetLicense],
  components: {
    SnackbarComponent,
  },
  data() {
    return {
      selectedType: "ANNUAL",
      loading: false,
      portalLoading: false,
      exposedApiLoading: false,
      autoCloseLoading: false,
      autoCloseSaving: false,
      autoCloseDays: 7,
      ticketCcEmailIds: [],
      portalMaintenanceEnabled: false,
      portalReleaseNotesEnabled: false,
      defaultShowApiCallCost: false,
      exposedApiConfig: buildExposedApiConfig(),
      snackbar: {
        show: false,
        message: "",
        color: "success",
      },
      grace_period: "",
      annual: {
        amount: "",
        users: "",
      },
      trial: {
        days: "",
        users: "",
      },
    };
  },
  async mounted() {
    await this.fetchLicense();
    await this.bindLicense();
    this.loadPortalSettings();
    this.loadExposedApiSettings();
    await this.loadAutoCloseDays();
  },
  methods: {
    allowNumbersOnly(e) {
      const key = e.key;
      if (!/^[0-9]$/.test(key)) {
        e.preventDefault();
      }
    },

    // sanitize pasted content
    sanitizeNumberInput(modelKey, value) {
      this[modelKey] = value.replace(/\D/g, "");
    },
    bindLicense() {
      console.log(this.LicenseList);
      if (!this.LicenseList) return;

      // Always bind Annual values
      this.annual.amount = this.LicenseList.annual_plan_price || "";
      this.annual.users = this.LicenseList.base_user_count || "";
      this.grace_period = this.LicenseList.default_grace_period_days || "";
      this.trial.users = this.LicenseList.trial_user_count || "";
      // Always bind Trial values
      this.trial.days = this.LicenseList.default_trial_days || "";
      this.ticketCcEmailIds = this.normalizeCcEmails(
        this.LicenseList.ticket_cc_email_ids
      );
      this.portalMaintenanceEnabled = this.toBooleanFlag(
        this.LicenseList.is_portal_under_maintenance ??
          this.LicenseList.portal_under_maintenance
      );
      this.portalReleaseNotesEnabled = this.toBooleanFlag(
        this.LicenseList.is_release_notes_enabled ??
          this.LicenseList.release_notes_enabled ??
          this.LicenseList.portal_release_notes
      );
      this.loadExposedApiSettings();
    },

    toBooleanFlag(value) {
      return value === true || String(value).trim().toLowerCase() === "true";
    },

    getExposedApiLabel(apiName) {
      return EXPOSED_API_LABELS[apiName] || apiName;
    },

    loadExposedApiSettings() {
      this.defaultShowApiCallCost = this.toBooleanFlag(
        this.LicenseList?.default_show_api_call_cost
      );
      this.exposedApiConfig = buildExposedApiConfig(
        parseDefaultApiEndpointSettings(
          this.LicenseList?.default_api_endpoint_settings ??
            this.LicenseList?.exposed_api_config
        )
      );
    },

    async saveExposedApiSettings() {
      this.exposedApiLoading = true;
      const default_show_api_call_cost = this.defaultShowApiCallCost === true;
      const default_api_endpoint_settings = rowsToDefaultApiEndpointSettings(
        this.exposedApiConfig
      );

      try {
        const response = await client.graphql({
          query: set_bms_global_settings,
          variables: {
            input: {
              default_api_endpoint_settings: JSON.stringify(
                default_api_endpoint_settings
              ),
              default_show_api_call_cost,
            },
          },
        });
        const responseData = JSON.parse(response.data.set_bms_global_settings);
        if (responseData.status != "SUCCESS") {
          this.snackbar = {
            show: true,
            message:
              responseData.status_message ||
              "Failed to save exposed API settings",
            color: "error",
          };
          return;
        }

        if (this.LicenseList) {
          this.LicenseList.default_api_endpoint_settings =
            default_api_endpoint_settings;
          this.LicenseList.default_show_api_call_cost =
            default_show_api_call_cost;
        }

        this.snackbar = {
          show: true,
          message:
            responseData.status_message || "Exposed API settings saved",
          color: "success",
        };
      } catch (error) {
        console.error("Error saving exposed API settings:", error);
        this.snackbar = {
          show: true,
          message: error.message || "Failed to save exposed API settings",
          color: "error",
        };
      } finally {
        this.exposedApiLoading = false;
      }
    },

    loadPortalSettings() {
      const maintenance =
        this.LicenseList?.is_portal_under_maintenance ??
        this.LicenseList?.portal_under_maintenance;
      this.portalMaintenanceEnabled = this.toBooleanFlag(maintenance);
      this.portalReleaseNotesEnabled = this.toBooleanFlag(
        this.LicenseList?.is_release_notes_enabled ??
          this.LicenseList?.release_notes_enabled ??
          this.LicenseList?.portal_release_notes
      );
    },

    async savePortalSettings() {
      this.portalLoading = true;
      const portal_under_maintenance = this.toBooleanFlag(
        this.portalMaintenanceEnabled
      );
      const release_notes_enabled = this.toBooleanFlag(
        this.portalReleaseNotesEnabled
      );
      try {
        const response = await client.graphql({
          query: set_portal_maintenance,
          variables: {
            input: {
              portal_under_maintenance,
            },
          },
        });
        const responseData = JSON.parse(response.data.set_portal_maintenance);
        if (responseData.status != "SUCCESS") {
          this.snackbar = {
            show: true,
            message:
              responseData.status_message || "Failed to save portal settings",
            color: "error",
          };
          return;
        }

        if (this.LicenseList && !Array.isArray(this.LicenseList)) {
          this.LicenseList.is_portal_under_maintenance =
            portal_under_maintenance;
          this.LicenseList.portal_under_maintenance = portal_under_maintenance;
        }
        this.portalMaintenanceEnabled = portal_under_maintenance;

        const releaseNotesResponse = await client.graphql({
          query: set_portal_release_notes,
          variables: {
            input: {
              release_notes_enabled,
            },
          },
        });
        const rawReleaseNotes =
          releaseNotesResponse.data.set_portal_release_notes;
        const releaseNotesData =
          typeof rawReleaseNotes === "string"
            ? JSON.parse(rawReleaseNotes)
            : rawReleaseNotes || {};
        if (releaseNotesData.status != "SUCCESS") {
          this.snackbar = {
            show: true,
            message:
              releaseNotesData.status_message ||
              "Failed to save portal release notes",
            color: "error",
          };
          return;
        }

        if (this.LicenseList && !Array.isArray(this.LicenseList)) {
          this.LicenseList.is_release_notes_enabled = release_notes_enabled;
          this.LicenseList.release_notes_enabled = release_notes_enabled;
          this.LicenseList.portal_release_notes = release_notes_enabled;
        }
        this.portalReleaseNotesEnabled = release_notes_enabled;

        localStorage.setItem(
          "rcs_portal_maintenance_enabled",
          String(portal_under_maintenance)
        );
        localStorage.setItem(
          "rcs_portal_release_notes_enabled",
          String(release_notes_enabled)
        );
        this.snackbar = {
          show: true,
          message:
            releaseNotesData.status_message ||
            responseData.status_message ||
            "Portal settings saved",
          color: "success",
        };
      } catch (error) {
        console.error("Error saving portal settings:", error);
        this.snackbar = {
          show: true,
          message:
            (error.errors && error.errors[0]?.message) ||
            error.message ||
            "Failed to save portal settings",
          color: "error",
        };
      } finally {
        this.portalLoading = false;
      }
    },

    async loadAutoCloseDays() {
      this.autoCloseLoading = true;
      try {
        const response = await client.graphql({
          query: get_auto_close_days,
        });
        const responseData = JSON.parse(response.data.get_auto_close_days);
        if (responseData.status == "SUCCESS") {
          const data = responseData.data ?? responseData;
          const days =
            typeof data === "number"
              ? data
              : Number(
                  data.ticket_auto_close_days ??
                    data.days ??
                    data.autoCloseDays ??
                    data.auto_close_days
                );
          if (Number.isFinite(days) && days >= 1) {
            this.autoCloseDays = days;
          }
        }
      } catch (error) {
        console.error("Error loading auto-close days:", error);
        this.snackbar = {
          show: true,
          message: error.message || "Failed to load auto-close days",
          color: "error",
        };
      } finally {
        this.autoCloseLoading = false;
      }
    },

    normalizeCcEmails(value) {
      const list = Array.isArray(value)
        ? value
        : typeof value === "string"
          ? value.split(/[,;\s]+/)
          : [];
      return [
        ...new Set(
          list
            .map((email) => String(email || "").trim().toLowerCase())
            .filter(Boolean)
        ),
      ];
    },

    isValidEmail(email) {
      return /.+@.+\..+/.test(email);
    },

    getTicketCcEmailIds() {
      return this.normalizeCcEmails(this.ticketCcEmailIds);
    },

    async saveAutoCloseDays() {
      const days = Number(this.autoCloseDays);
      if (!Number.isFinite(days) || days < 1 || !Number.isInteger(days)) {
        this.snackbar = {
          show: true,
          message: "Enter a valid number of days (1 or more).",
          color: "warning",
        };
        return;
      }

      const ticket_cc_email_ids = this.getTicketCcEmailIds();
      const invalidEmail = ticket_cc_email_ids.find(
        (email) => !this.isValidEmail(email)
      );
      if (invalidEmail) {
        this.snackbar = {
          show: true,
          message: `Enter a valid CC email (${invalidEmail}).`,
          color: "warning",
        };
        return;
      }

      this.autoCloseSaving = true;
      const input = {
        ticket_auto_close_days: days,
      };
      try {
        const response = await client.graphql({
          query: set_auto_close_days,
          variables: {
            input: input,
          },
        });
        const responseData = JSON.parse(response.data.set_auto_close_days);
        if (responseData.status != "SUCCESS") {
          this.snackbar = {
            show: true,
            message:
              responseData.status_message || "Failed to update auto-close days",
            color: "error",
          };
          return;
        }

        const ccResponse = await client.graphql({
          query: set_bms_global_settings,
          variables: {
            input: {
              ticket_cc_email_ids,
            },
          },
        });
        const ccResponseData = JSON.parse(
          ccResponse.data.set_bms_global_settings
        );
        if (ccResponseData.status != "SUCCESS") {
          this.snackbar = {
            show: true,
            message:
              ccResponseData.status_message ||
              "Failed to save ticket CC emails",
            color: "error",
          };
          return;
        }

        this.autoCloseDays = days;
        this.ticketCcEmailIds = ticket_cc_email_ids;
        this.snackbar = {
          show: true,
          message:
            ccResponseData.status_message ||
            responseData.status_message ||
            `RCS Support settings saved.`,
          color: "success",
        };
      } catch (error) {
        console.error("Error updating RCS Support settings:", error);
        this.snackbar = {
          show: true,
          message: error.message || "Failed to update RCS Support settings",
          color: "error",
        };
      } finally {
        this.autoCloseSaving = false;
      }
    },

    async saveSettings() {
      this.loading = true;
      try {
        const payload = {
          annual_plan_price: Number(this.annual.amount),
          base_user_count: Number(this.annual.users),
          default_grace_period_days: Number(this.grace_period),
          default_trial_days: Number(this.trial.days),
          trial_user_count: Number(this.trial.users),
          ticket_cc_email_ids: this.getTicketCcEmailIds(),
        };

        const response = await client.graphql({
          query: set_bms_global_settings,
          variables: {
            input: payload,
          },
        });
        const responseData = JSON.parse(response.data.set_bms_global_settings);

        if (responseData.status == "SUCCESS") {
          this.snackbar = {
            show: true,
            message:
              responseData.status_message || "Category created successfully",
            color: "success",
          };
        } else {
          this.snackbar = {
            show: true,
            message: responseData.status_message || "Failed to create category",
            color: "error",
          };
        }
        console.log("✅ License settings saved successfully");
      } catch (error) {
        console.error("❌ Error saving license settings", error);
      } finally {
        this.loading = false; // ✅ STOP loader (always)
      }
    },
  },
};
</script>



<style scoped>
.setting-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
  border: 1px solid #e8edf2;
  border-radius: 12px;
  padding: 20px 22px;
  background: #fff;
}

.setting-info {
  min-width: 0;
  flex: 1;
  max-width: 720px;
}

.setting-title {
  font-size: 16px;
  color: black;
  margin-bottom: 6px;
}

.setting-desc {
  font-size: 13px;
  line-height: 1.45;
  color: rgba(0, 0, 0, 0.55);
}

.setting-controls {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.days-input {
  width: 72px;
}

.days-input :deep(.v-field) {
  background: #f5f7fa;
  border-radius: 8px;
}

.days-input :deep(input) {
  text-align: center;
}

.days-label {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.55);
}

.cc-email-controls {
  width: min(420px, 100%);
}

.cc-email-input {
  width: 100%;
}

@media (max-width: 700px) {
  .setting-controls {
    width: 100%;
    justify-content: flex-end;
  }

  .cc-email-controls {
    width: 100%;
  }
}
</style>
